import { useMemo } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import AddIcon from '@mui/icons-material/Add';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import SearchOffOutlinedIcon from '@mui/icons-material/SearchOffOutlined';
import TravelExploreOutlinedIcon from '@mui/icons-material/TravelExploreOutlined';

function formatEmailState(state) {
    if (!state) return '';
    const value = String(state).toLowerCase();
    if (value === 'undeliverable') return 'Undeliverable';
    if (value === 'added') return 'Added';
    if (value === 'no_emails_found') return 'No emails found';
    if (value === 'blocked') return 'Blocked';
    return state;
}

function emailStateStyles(state) {
    const value = String(state || '').toLowerCase();
    if (value === 'undeliverable') {
        return { bgcolor: '#ffebee', color: '#c62828' };
    }
    if (value === 'added') {
        return { bgcolor: '#e8f5e9', color: '#2e7d32' };
    }
    if (value === 'no_emails_found') {
        return { bgcolor: '#fff3e0', color: '#ef6c00' };
    }
    if (value === 'blocked') {
        return { bgcolor: '#fce4ec', color: '#ad1457' };
    }
    return { bgcolor: '#f5f5f5', color: '#616161' };
}

function mapCommodityResponse(apiData) {
    if (!apiData?.value?.length) return [];

    const rows = [];

    apiData.value.forEach((commodity, commodityIndex) => {
        (commodity.Exports || []).forEach((exp, exportIndex) => {
            const trader = exp.Trader;
            if (!trader?.CompanyName) return;

            rows.push({
                key: `${commodityIndex}-${exportIndex}-${trader.TraderId || ''}-${trader.CompanyName}-${exp.MonthId || ''}`,
                companyName: trader.CompanyName,
                postcode: trader.PostCode || '',
                domain: trader.Domain || '',
                emailState: trader.EmailState || '',
                emails: Array.isArray(trader.Emails) ? trader.Emails : [],
                address: [trader.Address1, trader.Address2, trader.Address3]
                    .filter(Boolean)
                    .join(', '),
                commodityCode: commodity.Hs6Code || commodity.Cn8Code || '',
                commodityDescription:
                    commodity.Cn8LongDescription ||
                    commodity.Hs6Description ||
                    commodity.Hs4Description ||
                    '',
                monthId: exp.MonthId || '',
            });
        });
    });

    return rows;
}

const headerCellSx = {
    py: 1.5,
    px: 2,
    bgcolor: '#fafafa',
    borderBottom: '1px solid #eee',
    whiteSpace: 'nowrap',
};

const bodyCellSx = {
    py: 1.5,
    px: 2,
    borderBottom: '1px solid #f0f0f0',
    verticalAlign: 'middle',
};

function TableComponent({ apiData, progress, noResults, handleAdd, theme, hasActiveSearch = false }) {
    const tableData = useMemo(() => mapCommodityResponse(apiData), [apiData]);

    const columns = [
        'Company Name',
        'Company Domain',
        'Postcode',
        'Commodity Code',
        'Commodity Description',
        'Month ID',
        'Actions',
    ];

    return (
        <TableContainer
            sx={{
                border: '1px solid #eee',
                borderRadius: '12px',
                overflow: 'hidden',
                bgcolor: '#fff',
            }}
        >
            <Table size="medium">
                <TableHead>
                    <TableRow>
                        {columns.map((label) => (
                            <TableCell
                                key={label}
                                align={label === 'Actions' ? 'right' : 'left'}
                                sx={headerCellSx}
                            >
                                <Typography
                                    variant="subtitle2"
                                    sx={{ fontWeight: 700, color: 'text.secondary', letterSpacing: 0.2 }}
                                >
                                    {label}
                                </Typography>
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>

                <TableBody>
                    {tableData.length === 0 && progress ? (
                        <TableRow>
                            <TableCell colSpan={7} align="center" sx={{ border: 0 }}>
                                <Box sx={{ py: 6, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
                                    <CircularProgress color="primary" size={36} />
                                    <Typography variant="body2" color="text.secondary">
                                        Searching companies...
                                    </Typography>
                                </Box>
                            </TableCell>
                        </TableRow>
                    ) : tableData.length === 0 ? (
                        <TableRow>
                            <TableCell colSpan={7} align="center" sx={{ border: 0 }}>
                                <Box sx={{ py: 6, px: 2, textAlign: 'center' }}>
                                    <Box
                                        sx={{
                                            width: 56,
                                            height: 56,
                                            borderRadius: '50%',
                                            bgcolor: '#fafafa',
                                            border: '1px solid #eee',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            mb: 1.5,
                                        }}
                                    >
                                        {hasActiveSearch ? (
                                            <SearchOffOutlinedIcon sx={{ color: 'text.secondary' }} />
                                        ) : (
                                            <TravelExploreOutlinedIcon sx={{ color: 'text.secondary' }} />
                                        )}
                                    </Box>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                                        {hasActiveSearch ? 'No results found' : 'Ready to search'}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {hasActiveSearch
                                            ? 'Try a different search term or search type.'
                                            : 'Enter a search above to find companies to add.'}
                                    </Typography>
                                </Box>
                            </TableCell>
                        </TableRow>
                    ) : (
                        <>
                            {tableData.map((row) => {
                            const emailState = String(row.emailState || '').toLowerCase();
                            const undeliverable = emailState === 'undeliverable';
                            const blocked = emailState === 'blocked';
                            const hasEmails = Array.isArray(row.emails) && row.emails.length > 0;
                            const addDisabled = !row.domain || undeliverable || blocked || !hasEmails;
                            const addTitle = undeliverable
                                ? 'Email is undeliverable for this company'
                                : blocked
                                    ? 'Domain is on the blocked list'
                                    : !row.domain
                                        ? 'No domain found'
                                        : !hasEmails
                                            ? 'No deliverable emails found'
                                            : 'Add emails';

                            return (
                                <TableRow
                                    key={row.key}
                                    hover
                                    sx={{
                                        '&:last-child td': { borderBottom: 0 },
                                        transition: 'background-color 0.15s ease',
                                    }}
                                >
                                    <TableCell sx={bodyCellSx}>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            {row.companyName}
                                        </Typography>
                                    </TableCell>

                                    <TableCell sx={bodyCellSx}>
                                        <Typography variant="body2" color={row.domain ? 'text.primary' : 'text.secondary'}>
                                            {row.domain || '—'}
                                        </Typography>
                                    </TableCell>

                                    <TableCell sx={bodyCellSx}>
                                        <Typography variant="body2" color="text.secondary">
                                            {row.postcode || '—'}
                                        </Typography>
                                    </TableCell>

                                    <TableCell sx={bodyCellSx}>
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                            {row.commodityCode || '—'}
                                        </Typography>
                                    </TableCell>

                                    <TableCell sx={bodyCellSx}>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{
                                                maxWidth: 260,
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                            }}
                                            title={row.commodityDescription || undefined}
                                        >
                                            {row.commodityDescription || '—'}
                                        </Typography>
                                    </TableCell>

                                    <TableCell sx={bodyCellSx}>
                                        <Typography variant="body2" color="text.secondary">
                                            {row.monthId || '—'}
                                        </Typography>
                                    </TableCell>

                                    <TableCell align="right" sx={bodyCellSx}>
                                        <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
                                            {row.emailState ? (
                                                <Chip
                                                    size="small"
                                                    label={formatEmailState(row.emailState)}
                                                    sx={{
                                                        ...emailStateStyles(row.emailState),
                                                        fontWeight: 600,
                                                        borderRadius: '8px',
                                                    }}
                                                />
                                            ) : null}
                                            <Tooltip title={addTitle}>
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        disabled={addDisabled}
                                                        onClick={() =>
                                                            handleAdd({
                                                                companyName: row.companyName,
                                                                postcode: row.postcode || 'N/A',
                                                                domain: row.domain,
                                                                emails: row.emails || [],
                                                                emailState: row.emailState || '',
                                                            })
                                                        }
                                                        sx={{
                                                            color: addDisabled ? 'text.disabled' : 'primary.main',
                                                            border: '1px solid',
                                                            borderColor: addDisabled ? '#eee' : 'primary.light',
                                                            borderRadius: '8px',
                                                            bgcolor: addDisabled ? '#fafafa' : 'rgba(255,0,0,0.04)',
                                                            '&:hover': {
                                                                bgcolor: 'rgba(255,0,0,0.08)',
                                                            },
                                                        }}
                                                    >
                                                        <AddIcon fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            );
                            })}
                            {progress ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center" sx={{ border: 0 }}>
                                        <Box sx={{ py: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5 }}>
                                            <CircularProgress color="primary" size={18} />
                                            <Typography variant="body2" color="text.secondary">
                                                Finding more companies...
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ) : null}
                        </>
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

export default TableComponent;
